import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSONContent } from '@tiptap/react';
import { useParams, useNavigate } from 'react-router-dom';
import { DossierEditor } from '../components/editor/DossierEditor';
import { Trash2 } from 'lucide-react';
import { PagesRail } from '../components/scratchpad/PagesRail';
import { PanelEdgeTab } from '../components/PanelEdgeTab';
import { useConfirm } from '../contexts/ConfirmContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { PinButton } from '../components/PinButton';
import { excerptDeleteWarning } from '../lib/excerpts';
import {
  listScratchpads, createScratchpad, updateScratchpad, deleteScratchpad, parseContent,
  type Scratchpad,
} from '../lib/scratchpads';

const AUTOSAVE_MS = 600;

export function ScratchpadPage() {
  const { id: routeId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [pads, setPads] = useState<Scratchpad[]>([]);
  const [currentId, setCurrentId] = useState('');
  // #269: on a phone the rail slides over the editor, so it starts closed there.
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(isMobile);
  const [error, setError] = useState('');

  // Autosave state lives in refs so the unmount cleanup sees current values,
  // not values captured when the effect was created.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingSave = useRef<{ id: string; content: string } | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    listScratchpads()
      .then(async (loaded) => {
        if (cancelled) return;
        if (loaded.length === 0) {
          const first = await createScratchpad({});
          setPads([first]);
          setCurrentId(first.id);
          return;
        }
        setPads(loaded);
        // Honour a per-pad route (e.g. from a pinned scratchpad), falling
        // back to the newest pad when the param is absent or unknown.
        const target = routeId && loaded.some((p) => p.id === routeId) ? routeId : loaded[0].id;
        setCurrentId(target);
      })
      .catch(() => {
        // Covers both the list AND the first-pad create below it, so keep
        // the wording honest about that rather than blaming the load.
        if (!cancelled) setError('Could not open scratchpads. Check your connection and reload.');
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Write out whatever is pending, right now. Safe to call when nothing is.
  const flushSave = useCallback(() => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = undefined; }
    const p = pendingSave.current;
    pendingSave.current = undefined;
    if (!p) return;
    updateScratchpad(p.id, { content: p.content })
      .then(() => {
        // Without this, `pads` still holds the load-time content. Switching
        // away and back remounts the editor (key={currentId}) from `pads`,
        // silently reverting the edit just saved to the server.
        setPads((prev) => prev.map((x) => (x.id === p.id ? { ...x, content: p.content } : x)));
      })
      .catch(() => setError('Could not save.'));
  }, []);

  const handleNewPage = useCallback(async () => {
    flushSave();
    const made = await createScratchpad({});
    setPads((prev) => [made, ...prev]);
    setCurrentId(made.id);
  }, [flushSave]);

  const handleChange = useCallback((json: JSONContent) => {
    // Capture the id NOW: this payload belongs to the pad being edited,
    // even if the user switches before the timer fires.
    pendingSave.current = { id: currentId, content: JSON.stringify(json) };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, AUTOSAVE_MS);
  }, [currentId, flushSave]);

  const handleSelect = useCallback((id: string) => {
    flushSave();
    setCurrentId(id);
    // Keep the address bar in step, or a reload reopens the pad the URL still
    // names. `replace` so clicking through pads does not bury the back button.
    navigate(`/scratchpad/${id}`, { replace: true });
    // On a phone the rail covers the editor, so close it once a page is picked.
    if (isMobile) setCollapsed(true);
  }, [flushSave, navigate, isMobile]);

  // The loader effect runs once, so a route param that changes WITHOUT a
  // remount (clicking a second pinned pad while already here) would otherwise
  // leave the previous pad on screen. Sync on the param instead of refetching.
  useEffect(() => {
    if (!routeId || routeId === currentId) return;
    if (!pads.some((p) => p.id === routeId)) return;
    flushSave();
    setCurrentId(routeId);
  }, [routeId, currentId, pads, flushSave]);

  // Renaming. Pads default to a creation date, so without this every page is
  // an indistinguishable date string in the rail.
  const [renaming, setRenaming] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');

  const commitRename = useCallback(async () => {
    const title = draftTitle.trim();
    setRenaming(false);
    // An empty title would leave the pad unidentifiable and unrecoverable
    // from the rail, so refuse rather than accept it.
    if (!title || !currentId) return;
    const before = pads.find((p) => p.id === currentId);
    if (before && before.title === title) return;
    try {
      const saved = await updateScratchpad(currentId, { title });
      setPads((prev) => prev.map((p) => (p.id === currentId ? { ...p, title: saved.title } : p)));
    } catch {
      setError('Could not rename.');
    }
  }, [draftTitle, currentId, pads]);

  const handleDelete = useCallback(async () => {
    if (!currentId) return;
    const target = pads.find((p) => p.id === currentId);
    const ok = await confirm({
      title: `Delete "${target?.title ?? 'this page'}"?`,
      message: ['This cannot be undone. Anything written here, and any files attached to it, will be removed.', await excerptDeleteWarning('scratchpad', currentId)].filter(Boolean).join(' '),
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      await deleteScratchpad(currentId);
    } catch {
      // Leave the rail untouched: pretending it is gone when the server still
      // has it is worse than an honest failure.
      setError('Could not delete that page. Try again.');
      return;
    }

    const remaining = pads.filter((p) => p.id !== currentId);
    setPads(remaining);
    if (remaining.length > 0) {
      setCurrentId(remaining[0].id);
      navigate(`/scratchpad/${remaining[0].id}`, { replace: true });
    } else {
      // Never leave the user staring at nothing with no way back in.
      const fresh = await createScratchpad({});
      setPads([fresh]);
      setCurrentId(fresh.id);
      navigate(`/scratchpad/${fresh.id}`, { replace: true });
    }
  }, [currentId, pads, confirm, navigate]);


  useEffect(() => () => { flushSave(); }, [flushSave]);

  const current = pads.find((p) => p.id === currentId);

  return (
    <div className="flex h-full min-h-0 relative">
      <div className="flex-1 min-w-0 overflow-y-auto">
        {error && <div className="m-4 p-3 rounded-md bg-danger/10 text-danger text-sm">{error}</div>}
        {current && (
          <>
            <div className="flex items-center gap-2 min-w-0 px-3 sm:px-6 pt-5">
              {renaming ? (
                <input
                  autoFocus
                  aria-label="Page title"
                  className="min-w-0 flex-1 text-xl font-semibold bg-bg-input border border-border rounded-md px-2 py-0.5 text-text-primary focus:outline-none focus:border-accent"
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); void commitRename(); }
                    if (e.key === 'Escape') { e.preventDefault(); setRenaming(false); }
                  }}
                />
              ) : (
                <button
                  type="button"
                  aria-label="Rename page"
                  title={current.title}
                  className="min-w-0 truncate text-xl font-semibold text-text-primary hover:text-accent transition-colors text-left"
                  onClick={() => { setDraftTitle(current.title); setRenaming(true); }}
                >
                  {current.title}
                </button>
              )}
              <PinButton entityType="scratchpad" entityId={current.id} label={current.title} />
              <button
                type="button"
                aria-label="Delete page"
                title="Delete page"
                onClick={handleDelete}
                className="p-1.5 rounded-md text-text-muted hover:text-danger hover:bg-bg-hover transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </div>
            <div className="px-3 sm:px-6 pb-6">
              <DossierEditor
                key={currentId}
                content={parseContent(current.content)}
                onUpdate={handleChange}
                scratchpadId={current.id}
                excerptHost={{ type: 'scratchpad', id: current.id }}
              />
            </div>
          </>
        )}
      </div>
      {/* Edge tab, matching the meeting right panel: a small tab rather than a
          full-height bar, and it slides away as the rail opens. */}
      <PanelEdgeTab
        action="open"
        visible={collapsed}
        onClick={() => setCollapsed(false)}
        title="Open pages"
        ariaLabel="Expand pages"
      />
      <PagesRail
        pads={pads}
        currentId={currentId}
        onSelect={handleSelect}
        onNewPage={handleNewPage}
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((c) => !c)}
      />
      {/* Phone only: the open rail overlays the editor; tap outside to close. */}
      {isMobile && !collapsed && (
        <div
          className="absolute inset-0 z-20 bg-black/50"
          onClick={() => setCollapsed(true)}
          aria-hidden="true"
          data-testid="pages-scrim"
        />
      )}
    </div>
  );
}
